import { useEffect, useState } from 'react';
import { Activity, User, ShieldCheck, Cog } from 'lucide-react';
import { Card, NotificationCard, UniversalLoadingState, StatusBadge, type StatusTone } from '@afrilink/ui';
import {
  customerActivityService,
  type ActivityEntry,
} from '@/domains/activity/services/customerActivityService';

const FRIENDLY_LABEL: Record<string, string> = {
  // Billing
  'invoice.issued': 'Invoice issued',
  'payment.recorded': 'Payment received',
  'payment.manual_recorded': 'Manual payment recorded',
  'invoice.transitioned_to_grace': 'Invoice entered grace period',
  'invoice.transitioned_to_paid': 'Invoice marked paid',
  'invoice.transitioned_to_suspended': 'Invoice suspended',
  // Hosting
  'whm.account_created': 'Hosting account created',
  'whm.account_suspended': 'Hosting account suspended',
  'whm.account_unsuspended': 'Hosting account restored',
  'whm.account_modified': 'Hosting plan changed',
  'whm.account_removed': 'Hosting account terminated',
  'whm.password_changed': 'cPanel password changed',
  'whm.cpanel_sso_issued': 'cPanel login link issued',
  // Plan change saga
  'plan_change.confirmed': 'Plan change confirmed',
  'plan_change.payment_received': 'Plan change payment received',
  'plan_change.compensated': 'Plan change reverted',
  // Domain
  'domain.registered': 'Domain registered',
  'domain.renewed': 'Domain renewed',
  'domain.transfer_confirmed': 'Domain transfer-in started',
  'domain.transfer_completed': 'Domain transfer-in complete',
  'domain.register_confirmed': 'Domain registration confirmed',
  'domain.register_payment_received': 'Domain registration payment received',
  'domain.renew_invoice_issued': 'Domain renewal invoice issued',
  'domain.renew_payment_received': 'Domain renewal payment received',
  'domain.epp_emailed': 'EPP code emailed to registrant',
  'domain.epp_retrieved': 'EPP code retrieved by staff',
  'domain.contacts_updated': 'Domain contacts updated',
  'domain.nameservers_updated': 'Domain nameservers updated',
  'domain.locked': 'Domain locked at registry',
  'domain.unlocked': 'Domain unlocked at registry',
  'domain.autorenew_enabled': 'Auto-renew enabled',
  'domain.autorenew_disabled': 'Auto-renew disabled',
  'domain_link.update': 'Domain record updated',
  'domain_link.insert': 'Domain linked to your account',
  // Customer
  'customer.password_reset_requested': 'Password reset requested',
  'customer.invite_resent': 'Welcome email resent',
};

const ACTOR_TONE: Record<ActivityEntry['actor_kind'], StatusTone> = {
  customer: 'info',
  staff: 'pending',
  system: 'draft',
};

const ACTOR_ICON: Record<ActivityEntry['actor_kind'], React.ComponentType<{ className?: string }>> = {
  customer: User,
  staff: ShieldCheck,
  system: Cog,
};

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h ago`;
  if (ms < 30 * 86_400_000) return `${Math.round(ms / 86_400_000)} d ago`;
  return new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function summariseEntry(e: ActivityEntry): { title: string; detail: string | null } {
  const title = FRIENDLY_LABEL[e.event] ?? e.event.replace(/[._]/g, ' ');
  // Try to extract a useful one-line detail from the after-payload
  let detail: string | null = null;
  const after = e.after ?? {};
  if (typeof after === 'object' && after !== null) {
    const a = after as Record<string, unknown>;
    if (typeof a.invoice_number === 'string') detail = `Invoice ${a.invoice_number}`;
    else if (typeof a.domain === 'string') detail = a.domain;
    else if (typeof a.cpanel_username === 'string') detail = `cPanel ${a.cpanel_username}`;
    else if (typeof a.new_plan_slug === 'string') detail = `→ ${a.new_plan_slug}`;
    else if (typeof a.domain_name === 'string') detail = a.domain_name;
  }
  return { title, detail };
}

export default function SettingsActivityPage() {
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customerActivityService
      .list(100)
      .then(setEntries)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <div className="space-y-4">
      <Card interactive={false} className="p-4">
        <h2 className="text-sm font-semibold text-navy-primary">Activity</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The last 100 events on your account — invoices, payments, hosting changes, domain
          updates. Read-only. Need a record exported? Open a support ticket.
        </p>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load activity" body={error} />}

      {!entries && !error && <UniversalLoadingState variant="spinner" caption="Loading…" />}

      {entries && entries.length === 0 && (
        <Card interactive={false} className="p-12 text-center">
          <Activity className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium text-navy-primary">No activity yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Once you receive an invoice, register a domain, or make a change, it'll appear here.
          </p>
        </Card>
      )}

      {entries && entries.length > 0 && (
        <Card interactive={false}>
          <ol className="divide-y divide-border">
            {entries.map((e) => {
              const summary = summariseEntry(e);
              const Icon = ACTOR_ICON[e.actor_kind];
              return (
                <li key={e.id} className="flex items-start gap-3 p-4">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Icon className="h-4 w-4 text-foreground-soft" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <p className="text-sm font-medium text-navy-primary">{summary.title}</p>
                      <StatusBadge tone={ACTOR_TONE[e.actor_kind]}>{e.actor_kind}</StatusBadge>
                      <span className="text-xs text-muted-foreground">{formatRelative(e.created_at)}</span>
                    </div>
                    {summary.detail && (
                      <p className="mt-0.5 text-xs text-foreground-soft">{summary.detail}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </Card>
      )}
    </div>
  );
}
